import { ITelemetryNamespaceLogger } from '@fluidx/office-fluid-types';
import { ITelemetryBaseEvent, ITelemetryProperties } from '@fluidframework/common-definitions';
import { Event, TelemetryEvent } from '@fluidx/utilities';

const maxAllowedCharactersPerField = 10000;
const maxAllowedFieldsPerEvent = 100;

const getCappedErrorProps = (errorProps: TelemetryEvent) => {
  let cappedFields: string | undefined = undefined;
  if (typeof errorProps.message === 'string') {
    if (errorProps.message.length > maxAllowedCharactersPerField) {
      cappedFields = 'message';
      errorProps.message = errorProps.message.substr(0, maxAllowedCharactersPerField);
    }
  }

  if (typeof errorProps.stack === 'string') {
    if (errorProps.stack.length > maxAllowedCharactersPerField) {
      cappedFields = cappedFields ? cappedFields.concat(', stack') : 'stack';
      errorProps.stack = errorProps.stack.substr(0, maxAllowedCharactersPerField);
    }
  }

  return cappedFields ? { ...errorProps, cappedFields } : errorProps;
};

/**
 * Basic implementation of the Logger interface.
 */
export abstract class BaseLogger implements ITelemetryNamespaceLogger {
  private readonly persistentProps: ITelemetryProperties;

  constructor(persistentProps?: ITelemetryProperties, private namespace?: string) {
    this.persistentProps = persistentProps || {};
    this.namespace = namespace;
  }

  /**
   * Logs the event to the location determined by the implementation.
   */
  protected abstract logEvent(event: Event): void;

  public send(event: ITelemetryBaseEvent): void {
    this.sendWithNamespace(event, this.namespace);
  }

  /**
   * Calls logEvent to log the Telemetry Event.
   * Note- Since updatedEvent is a spread of persistentProps and the event,
   * props in persistentProps with the same name as event will be overridden by the props in event.
   * @param event The event we want to log
   * @param namespace A namespace to scope this event
   */
  public sendWithNamespace(event: ITelemetryBaseEvent, namespace: string | undefined) {
    // Namespace provided in the sendWithNamespace call has precedence
    // over the namespace specified while instantiating the logger.
    const loggerNamespace = namespace || this.namespace;

    // Update event to account for any persistentProps
    let { category, ...props } = { ...this.persistentProps, ...event };

    // Logging exceedsMaxAllowedFields to give an indication that the event exceeds max allowed fields in an event.
    const exceedsMaxAllowedFields = Object.keys(props).length > maxAllowedFieldsPerEvent ? true : false;

    // If the category of the event is error, there is a possibility that the message and stack may be huge
    // Truncating the message and stack in case it exceeds max allowed characters in a field.
    if (category === 'error' || category === 'Error') {
      props = getCappedErrorProps(props);
    }

    this.logEvent({
      category,
      props: {
        ...props,
        exceedsMaxAllowedFields
      },
      namespace: loggerNamespace
    });
  }
}

// cspell:ignore otel
